import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { useTranslation } from "react-i18next";
import Password from "../Login/Password";
import TipoCambio from "./TipoCambio";
import Alertas from "./Alertas";
import {
  IcMenu, IcChevL, IcChevR, IcChevD, IcChevU,
  IcUserCircle, IcLock, IcSignOut,
} from "../../Icons";

// ── SVG base (para los iconos de nav personalizados) ──────────────────────────
const S = ({ size = 18, children, style }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0, display: "inline-block", verticalAlign: "middle", ...style }}
  >
    {children}
  </svg>
);

// Contenedores — contenedor marítimo con nervaduras
const IcContenedores = () => (
  <S>
    <rect x="2.5" y="6" width="19" height="12" rx="1.2"/>
    <line x1="6.5"  y1="8.5" x2="6.5"  y2="15.5"/>
    <line x1="10.2" y1="8.5" x2="10.2" y2="15.5"/>
    <line x1="13.8" y1="8.5" x2="13.8" y2="15.5"/>
    <line x1="17.5" y1="8.5" x2="17.5" y2="15.5"/>
  </S>
);

// Catálogos — libro abierto
const IcCatalogos = () => (
  <S>
    <path d="M2 5.5A1.5 1.5 0 0 1 3.5 4H9a3 3 0 0 1 3 3v13a2.5 2.5 0 0 0-2.5-2.5H3.5A1.5 1.5 0 0 1 2 16z"/>
    <path d="M22 5.5A1.5 1.5 0 0 0 20.5 4H15a3 3 0 0 0-3 3v13a2.5 2.5 0 0 1 2.5-2.5h6A1.5 1.5 0 0 0 22 16z"/>
  </S>
);

// Admin — documento con tendencia (estado de cuenta / abonos)
const IcAdmin = () => (
  <S>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <polyline points="7 16 10 13 13 15.5 17 11"/>
    <circle cx="17" cy="11" r="1" fill="currentColor" strokeWidth="0"/>
  </S>
);

// Costo de importación — billetera con moneda
const IcGastos = () => (
  <S>
    <path d="M20 8H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2z"/>
    <path d="M16 8V6a2 2 0 0 0-2-2H10a2 2 0 0 0-2 2v2"/>
    <path d="M22 13h-4a2 2 0 0 0 0 4h4"/>
    <circle cx="18" cy="15" r=".8" fill="currentColor" strokeWidth="0"/>
  </S>
);

// Reportes — barras ascendentes
const IcReportes = () => (
  <S>
    <line x1="3" y1="21" x2="21" y2="21"/>
    <rect x="4"  y="13" width="4" height="8" rx="1"/>
    <rect x="10" y="8"  width="4" height="13" rx="1"/>
    <rect x="16" y="4"  width="4" height="17" rx="1"/>
  </S>
);

// Usuarios — persona con check
const IcUsuarios = () => (
  <S>
    <circle cx="10" cy="7" r="4"/>
    <path d="M3 21v-1a7 7 0 0 1 7-7h1.5"/>
    <path d="M17 13l1 1 3-3" strokeWidth="1.8"/>
    <circle cx="19" cy="17" r="3.5"/>
  </S>
);

// Logs — portapapeles con lista
const IcLogs = () => (
  <S>
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
    <rect x="8" y="2" width="8" height="4" rx="1"/>
    <circle cx="9" cy="11" r="1" fill="currentColor" strokeWidth="0"/>
    <line x1="12" y1="11" x2="17" y2="11"/>
    <circle cx="9" cy="15.5" r="1" fill="currentColor" strokeWidth="0"/>
    <line x1="12" y1="15.5" x2="17" y2="15.5"/>
  </S>
);

// ── Navegación agrupada por sección (Moy Logistics) ──────────────────────────
// `label` pasa por i18n (llaves en translations/en/global.json).
const NAV_SECTIONS = [
  {
    section: "Operación",
    items: [
      { label: "Contenedores", icon: <IcContenedores />, href: "/MenuContenedores", perm: "contenedores" },
      { label: "Catálogos",    icon: <IcCatalogos />,    href: "/MenuCatalogos",    perm: "menuCatalogos" },
    ],
  },
  {
    section: "Finanzas",
    items: [
      { label: "Finanzas", icon: <IcAdmin />, href: "/Finanzas", perm: "menuAdmin" },
      { label: "Costo Importacion", icon: <IcGastos />, href: "/MenuGastos", perm: "menu_gastos" },
    ],
  },
  {
    section: "Análisis",
    items: [
      { label: "Reportes", icon: <IcReportes />, href: "/MenuReportes", perm: "menuReportes" },
    ],
  },
  {
    section: "Administración",
    items: [
      { label: "Usuarios", icon: <IcUsuarios />, href: "/ListadoUsuarios", perm: "menu_usuarios" },
      { label: "Logs",     icon: <IcLogs />,     href: "/MenuLogs",        perm: "menu_logs" },
    ],
  },
];

const IDIOMAS = [
  { code: "es", label: "ES", flag: "/flagES.png" },
  { code: "en", label: "EN", flag: "/flagEN.png" },
];

const Header = () => {
  const { isAuth, user } = useContext(AuthContext);
  const [t, i18n] = useTranslation("global");

  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [modalPassword, setModalPassword] = useState(false);
  const [userMenuOpen,  setUserMenuOpen]  = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sidebar-collapsed") === "true"
  );

  const currentPath = window.location.pathname;
  const esCliente = user?.tipo === "Cliente"; // los clientes solo ven Contenedores
  const idioma = ["es", "en"].includes(i18n.language) ? i18n.language : (localStorage.getItem("idioma") || "es");

  // ── Body classes ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuth) return;
    document.body.classList.add("has-sidebar");
    if (collapsed) document.body.classList.add("sidebar-collapsed");
    return () => {
      document.body.classList.remove("has-sidebar");
      document.body.classList.remove("sidebar-collapsed");
    };
  }, [isAuth]); // eslint-disable-line

  if (!isAuth || !user) return null;

  // ── Helpers ───────────────────────────────────────────────────────────────
  const hasPerm = (perm) => {
    if (perm === "contenedores") {
      return !!(user.menuContenedores || user.menuContenedoresPS || user.menuContenedoresClientes || user.menuContenedoresEliezer);
    }
    return !!user[perm];
  };

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("sidebar-collapsed", next);
    if (next) {
      document.body.classList.add("sidebar-collapsed");
      setUserMenuOpen(false);
    } else {
      document.body.classList.remove("sidebar-collapsed");
    }
  };

  const cambiaIdioma = (code) => {
    localStorage.setItem("idioma", code);
    i18n.changeLanguage(code);
  };

  return (
    <>
      {/* ── Mobile top bar ──────────────────────────────────────────────── */}
      <div className="sidebar-topbar d-lg-none">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
          <IcMenu />
        </button>
        <a href="/Inicio">
          <img src={process.env.PUBLIC_URL + "/logoInstitucion.png"} height="34" alt="logo" />
        </a>
      </div>

      {/* ── Mobile overlay ──────────────────────────────────────────────── */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside
        className={[
          "app-sidebar",
          sidebarOpen ? "open"      : "",
          collapsed   ? "collapsed" : "",
        ].filter(Boolean).join(" ")}
      >
        {/* Brand */}
        <div className="sidebar-brand">
          <a href="/Inicio" className="sidebar-logo-link">
            <img
              src={process.env.PUBLIC_URL + "/logoInstitucion.png"}
              alt="logo"
              style={{ maxHeight: 44, width: "auto" }}
            />
          </a>
          <button
            className="sidebar-collapse-btn"
            onClick={toggleCollapse}
            title={collapsed ? "Expandir" : "Comprimir"}
          >
            {collapsed ? <IcChevR /> : <IcChevL />}
          </button>
        </div>

        {/* Nav */}
        <nav className="sidebar-nav">
          {NAV_SECTIONS.map((sec) => {
            const visibleItems = sec.items.filter((i) =>
              esCliente ? i.perm === "contenedores" : hasPerm(i.perm)
            );
            if (visibleItems.length === 0) return null;

            return (
              <div key={sec.section} className="sidebar-section-group">
                <div className="sidebar-section">{t(sec.section)}</div>

                {visibleItems.map((item) => {
                  const isActive = currentPath === item.href;
                  const label = esCliente && item.perm === "contenedores" ? t("Mis Contenedores") : t(item.label);
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      className={`sidebar-item${isActive ? " active" : ""}`}
                      title={collapsed ? label : ""}
                    >
                      {item.icon}
                      <span className="sidebar-label">{label}</span>
                    </a>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="sidebar-footer">
          {!esCliente && user.menuContenedores ? <Alertas /> : null}
          {!esCliente ? <TipoCambio /> : null}
          <div className="sidebar-lang">
            {IDIOMAS.map((l) => (
              <button
                key={l.code}
                type="button"
                className={idioma === l.code ? "active" : ""}
                onClick={() => cambiaIdioma(l.code)}
              >
                <img src={process.env.PUBLIC_URL + l.flag} alt="" />
                {l.label}
              </button>
            ))}
          </div>

          <div
            className="sidebar-item"
            onClick={() => !collapsed && setUserMenuOpen(!userMenuOpen)}
            title={collapsed ? (user.nombre || user.email || t("Usuario")) : ""}
            style={{ justifyContent: collapsed ? "center" : "space-between" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
              <IcUserCircle />
              <span className="sidebar-label" style={{ fontSize: "0.78rem" }}>
                {[user.nombre, user.apellido].filter(Boolean).join(" ") || user.email || t("Usuario")}
              </span>
            </div>
            {!collapsed && (
              userMenuOpen ? <IcChevD /> : <IcChevU />
            )}
          </div>

          {userMenuOpen && !collapsed && (
            <div className="sidebar-submenu">
              <div
                className="sidebar-item sidebar-item-sm"
                onClick={() => { setModalPassword(true); setUserMenuOpen(false); }}
              >
                <IcLock />
                <span>{t("Cambiar contraseña")}</span>
              </div>
              <a href="/Logout" className="sidebar-item sidebar-item-sm" style={{ color: "#f87171" }}>
                <IcSignOut />
                <span>{t("Cerrar sesión")}</span>
              </a>
            </div>
          )}
        </div>
      </aside>

      {/* ── Password modal ───────────────────────────────────────────────── */}
      <Modal size="sm" isOpen={modalPassword} toggle={() => setModalPassword(false)}>
        <ModalHeader toggle={() => setModalPassword(false)}>
          {t("Cambio de contraseña")}
        </ModalHeader>
        <ModalBody>
          <Password />
        </ModalBody>
      </Modal>
    </>
  );
};

export default Header;
